import { FloatingFormItem, InputDecimal } from '@/components/_common';
import ActionIcon from '@/components/_common/ActionIcon';
import {
  usePurchaseItemsRemoveWorkOrderLink,
  usePurchaseItemsWorkOrderLink,
} from '@/core/domain/purchase/purchaseItem/PurchaseItem.hooks';
import { PurchaseItem } from '@/core/domain/purchase/purchaseItem/PurchaseItem.types';
import { useWorkOrderComplement } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.hooks';
import { useWorkOrderItems } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.hooks';
import {
  WorkOrderItem,
  WorkOrderItemType,
} from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { useDefaultEmptyFieldError, useDisclosure } from '@/hooks';
import formatterPrice from '@/utils/number/formatterPrice';
import {
  Alert,
  Card,
  Col,
  Descriptions,
  Divider,
  Form,
  Modal,
  Row,
  Skeleton,
  Table,
  Tooltip,
} from 'antd';
import { useMemo, useState } from 'react';
import { Link, XCircle } from 'react-feather';
import PurchaseItemCreateInWorkorder from './PurchaseItemCreateInWorkorder';

interface Props {
  complementId?: number;
  purchaseItem?: PurchaseItem;
  partIdsLinked: number[];
  isOpened: boolean;
}

export default function PurchaseItemWorkOrderLink({
  partIdsLinked,
  complementId,
  purchaseItem,
  isOpened,
}: Props) {
  const [form] = Form.useForm<{ quantity: number }>();
  const requiredField = useDefaultEmptyFieldError();

  const [selectedWorkOrderItems, setSelectedWorkOrderItems] = useState<
    WorkOrderItem[]
  >([]);

  const [opened, action] = useDisclosure();

  const workOrderComplement = useWorkOrderComplement(complementId, opened);

  const workOrderItemsData = useWorkOrderItems(
    workOrderComplement.data?.id,
    false,
    WorkOrderItemType.Part,
    false,
  );

  const workOrderItems = workOrderItemsData.data?.data || [];

  const purchaseItemWorkOrderLink = usePurchaseItemsWorkOrderLink();
  const itemRemoveWorkOrderLink = usePurchaseItemsRemoveWorkOrderLink();

  const handleClose = () => {
    setSelectedWorkOrderItems([]);
    form.resetFields();
    action.close();
  };

  const handleSubmit = async () => {
    try {
      if (!purchaseItem) return;

      const workOrderItem = selectedWorkOrderItems[0];
      const values = await form.validateFields();

      await purchaseItemWorkOrderLink.mutateAsync({
        purchaseItemId: purchaseItem.id,
        quantity: values.quantity,
        workOrderItemId: workOrderItem.id,
      });

      await workOrderItemsData.refetch();

      handleClose();
    } catch {}
  };

  const handleUnlinkWorkOrderItem = () => {
    Modal.confirm({
      title: 'Remover vinculo da Ordem de Serviço',
      content: (
        <>
          <p>
            Deseja remover o vinculo da peça{' '}
            <strong>"{purchaseItem?.name}"</strong> do item da Ordem de Serviço?
          </p>

          {!isOpened && (
            <Alert
              type="warning"
              description={
                <>
                  <strong>Atenção:</strong> A compra está autorizada, a remoção
                  do vínculo da peça do orçamento é irreversível.
                  <br />A peça deixa de pertencer ao orçamento e passa a ser
                  considerada como item de estoque.
                </>
              }
            />
          )}
        </>
      ),
      onOk: async () => {
        if (purchaseItem) {
          await itemRemoveWorkOrderLink.mutateAsync(purchaseItem.id);
          await workOrderItemsData.refetch();
          return;
        }
        handleClose();
      },
      okButtonProps: { danger: true },
      okText: 'Desvincular',
    });
  };

  const maxQuantity = useMemo(() => {
    if (!selectedWorkOrderItems[0] || !purchaseItem) return 0;

    const purchaseItemQuantity = purchaseItem?.quantity || 0;
    const workOrderItemRemaining =
      selectedWorkOrderItems?.[0]?.status?.remaining || 0;

    if (purchaseItemQuantity > workOrderItemRemaining) {
      return workOrderItemRemaining;
    }

    return purchaseItemQuantity;
  }, [purchaseItem?.quantity, selectedWorkOrderItems.length]);

  return (
    <>
      {!purchaseItem?.workOrderNumber ? (
        <Tooltip title="Vincular peça a OS">
          <ActionIcon onClick={action.open}>
            <Link size={20} />
          </ActionIcon>
        </Tooltip>
      ) : (
        <Tooltip title="Desvincular peça da OS">
          <ActionIcon onClick={handleUnlinkWorkOrderItem}>
            <XCircle size={20} color="red" />
          </ActionIcon>
        </Tooltip>
      )}

      <Divider style={{ margin: 0 }} type="vertical" />

      <Modal
        title="Vincular item da compra a um item da ordem de serviço"
        open={opened}
        onCancel={handleClose}
        width={800}
        okText="Vincular item"
        onOk={handleSubmit}
        destroyOnClose
        okButtonProps={{ disabled: !selectedWorkOrderItems.length }}
        confirmLoading={purchaseItemWorkOrderLink.isLoading}
      >
        <Form form={form} initialValues={{ quantity: 0 }}>
          <Card
            title="Item da compra"
            style={{ marginBottom: 16, borderRadius: 8, overflow: 'hidden' }}
            bodyStyle={{ padding: 0 }}
          >
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Descrição">
                {purchaseItem?.name}
              </Descriptions.Item>
              <Descriptions.Item label="Prç. Unitário">
                {formatterPrice(purchaseItem?.unitaryValue || 0)}
              </Descriptions.Item>
              <Descriptions.Item label="Qtde. da compra">
                {purchaseItem?.quantity}
              </Descriptions.Item>
              <Descriptions.Item label="Cód. Fabricante">
                {purchaseItem?.manufacturingCode}
              </Descriptions.Item>
              <Descriptions.Item label="Cód. Interno">
                {purchaseItem?.sku}
              </Descriptions.Item>
              <Descriptions.Item label="Cód. Empresa">
                {purchaseItem?.companyCode}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <PurchaseItemCreateInWorkorder
            purchaseItem={purchaseItem}
            onFinish={handleClose}
          />

          <Skeleton
            loading={
              workOrderComplement.isLoading || workOrderItemsData.isLoading
            }
          >
            <h3 style={{ margin: 0 }}>
              OS - {workOrderComplement.data?.complementIdentifier}
            </h3>
            <div>
              <strong style={{ display: 'block' }}>
                Selecione um item da OS para vincular ao item da compra
              </strong>
              <small style={{ display: 'block' }}>
                Só é permitido selecionar item da OS com a mesma referência (
                <code>partId</code>) a peça do item da compra.
              </small>
            </div>

            <Table
              dataSource={workOrderItems}
              scroll={{ x: true }}
              rowKey={(item) => item.id}
              rowSelection={{
                selectedRowKeys: selectedWorkOrderItems.map((item) => item.id),
                onChange: (_, rows) => {
                  setSelectedWorkOrderItems(rows[0] ? [rows[0]] : []);
                },
                getCheckboxProps: (row) => ({
                  disabled:
                    partIdsLinked.includes(row.itemId) ||
                    row.itemId !== purchaseItem?.partId ||
                    (!!selectedWorkOrderItems.length &&
                      !selectedWorkOrderItems.some(
                        (item) => item.id === row.id,
                      )),
                }),
              }}
              columns={[
                { title: 'Descrição', dataIndex: 'description', width: 300 },
                {
                  title: 'Cód. Fabricante',
                  dataIndex: 'manufacturingCode',
                  width: 100,
                },
                {
                  title: 'Cód. Interno',
                  dataIndex: 'internalCode',
                  width: 100,
                },
                { title: 'Cód. Empresa', dataIndex: 'companyCode', width: 100 },
                {
                  title: 'Solicitado',
                  width: 100,
                  dataIndex: 'quantity',
                  align: 'right',
                },
                {
                  title: 'Aguardando Cotação',
                  width: 100,
                  dataIndex: ['status', 'remaining'],
                  align: 'right',
                },
                {
                  title: 'Preço Unit.',
                  align: 'right',
                  render: (_, item) => formatterPrice(item.unitPrice || 0),
                },
              ]}
              pagination={false}
            />

            <Row style={{ marginTop: 24 }} justify="end">
              <Col span={24} sm={12} md={6}>
                <FloatingFormItem
                  name="quantity"
                  label="Qtde. de entrega"
                  style={{ margin: 0 }}
                  rules={[
                    requiredField,

                    {
                      validator: (_, value) => {
                        if (value < 0.01 || value > (maxQuantity || 0)) {
                          return Promise.reject(
                            Error(
                              `Informe um valor entre 0,01 a ${maxQuantity}`,
                            ),
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputDecimal
                    max={maxQuantity}
                    disabled={!selectedWorkOrderItems.length}
                  />
                </FloatingFormItem>
              </Col>
            </Row>
          </Skeleton>
        </Form>
      </Modal>
    </>
  );
}
